import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Board from "react-trello";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles, useTheme } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import BusinessCenterIcon from "@material-ui/icons/BusinessCenter";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DoneIcon from "@material-ui/icons/Done";
import EditIcon from "@material-ui/icons/Edit";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import FlagIcon from "@material-ui/icons/Flag";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import PersonIcon from "@material-ui/icons/Person";
import ReplayIcon from "@material-ui/icons/Replay";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";

import ConfirmationModal from "../../components/ConfirmationModal";
import AppliedFiltersBar from "../../components/AppliedFiltersBar";
import MetricsStrip from "../../components/MetricsStrip";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import RowActionsMenu from "../../components/RowActionsMenu";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import crmService from "../../services/crm";
import DealTicketsPanel from "./DealTicketsPanel";
import CrmToolbar from "./CrmToolbar";
import CrmFilterPopover from "./CrmFilterPopover";
import ContactAutocomplete from "./ContactAutocomplete";
import DealSummary from "./DealSummary";
import DealWorkspaceDialog from "./DealWorkspaceDialog";
import DealInlineEditor from "./DealInlineEditor";
import DealOutcomePanel from "./DealOutcomePanel";
import DealActivitiesPanel from "./DealActivitiesPanel";
import DealHistoryPanel from "./DealHistoryPanel";
import {
  ACTIVITY_STATUS_LABELS,
  ACTIVITY_TYPE_LABELS,
  DEAL_STATUS_LABELS,
  buildCrmBoardData,
  canManageCrmPipeline,
  getActiveStages,
  getDefaultStageId,
  getCrmRealtimeRefreshPlan,
  isActivityOverdue,
  isDealNextActionOverdue,
  mergeDealsById,
  normalizePipelineStages,
  summarizeDeals,
  toDateEndBoundary,
  toDateStartBoundary,
} from "./crmUtils";

const BOARD_LANE_WIDTH = 328;
const BOARD_CARD_WIDTH = BOARD_LANE_WIDTH - 28;
const DEALS_PAGE_SIZE = 40;

const EMPTY_DEAL_FILTERS = {
  searchParam: "",
  status: "",
  userId: "",
  contact: null,
  dateStart: "",
  dateEnd: "",
};

const EMPTY_PIPELINE_FORM = {
  id: null,
  name: "",
  description: "",
  order: 0,
  isActive: true,
  isDefault: false,
};

const EMPTY_STAGE_FORM = {
  id: null,
  name: "",
  color: "#2f80ed",
  order: "",
  isActive: true,
};

const EMPTY_DEAL_FORM = {
  id: null,
  title: "",
  description: "",
  amount: "",
  currency: "BRL",
  source: "",
  expectedCloseAt: "",
  pipelineId: "",
  stageId: "",
  contact: null,
  userId: "",
};

const EMPTY_ACTIVITY_FORM = {
  id: null,
  type: "task",
  title: "",
  description: "",
  dueAt: "",
  status: "pending",
  isNextAction: false,
  userId: "",
};

const getDealFormValues = (deal) => ({
  id: deal.id,
  title: deal.title || "",
  description: deal.description || "",
  amount: deal.amount ?? "",
  currency: deal.currency || "BRL",
  source: deal.source || "",
  expectedCloseAt: toDateInput(deal.expectedCloseAt),
  pipelineId: deal.pipelineId || "",
  stageId: deal.stageId || "",
  contact: deal.contact || null,
  userId: deal.userId || "",
});

const ACTIVITY_TYPES = [
  { value: "task", label: ACTIVITY_TYPE_LABELS.task },
  { value: "call", label: ACTIVITY_TYPE_LABELS.call },
  { value: "meeting", label: ACTIVITY_TYPE_LABELS.meeting },
  { value: "email", label: ACTIVITY_TYPE_LABELS.email },
  { value: "note", label: ACTIVITY_TYPE_LABELS.note },
  { value: "follow_up", label: ACTIVITY_TYPE_LABELS.follow_up },
];

const formatCurrency = (value, currency = "BRL") => {
  const numeric = Number(value || 0);

  if (Number.isNaN(numeric)) {
    return "R$ 0,00";
  }

  return numeric.toLocaleString("pt-BR", {
    style: "currency",
    currency: currency || "BRL",
  });
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("pt-BR");
};

const formatFilterDate = (value) => {
  const [year, month, day] = String(value || "").split("-");

  return year && month && day ? `${day}/${month}/${year}` : value;
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toDateInput = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
};

const toDateTimeInput = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const fromDateInput = (value) => {
  if (!value) {
    return null;
  }

  return new Date(`${value}T00:00:00`).toISOString();
};

const fromDateTimeInput = (value) => {
  if (!value) {
    return null;
  }

  return new Date(value).toISOString();
};

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const number = Number(value);
  return Number.isNaN(number) ? null : number;
};

const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== "string") {
    return `rgba(47, 128, 237, ${alpha})`;
  }

  const sanitized = hex.replace("#", "");
  const normalized =
    sanitized.length === 3
      ? sanitized
          .split("")
          .map((char) => char + char)
          .join("")
      : sanitized;
  const value = parseInt(normalized, 16);

  if (Number.isNaN(value)) {
    return `rgba(47, 128, 237, ${alpha})`;
  }

  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const getReadableColor = (hex) => {
  if (!hex || typeof hex !== "string") {
    return "#ffffff";
  }

  const sanitized = hex.replace("#", "");
  const normalized =
    sanitized.length === 3
      ? sanitized
          .split("")
          .map((char) => char + char)
          .join("")
      : sanitized;
  const value = parseInt(normalized, 16);

  if (Number.isNaN(value)) {
    return "#ffffff";
  }

  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  const yiq = (red * 299 + green * 587 + blue * 114) / 1000;

  return yiq >= 148 ? "#0f172a" : "#ffffff";
};

const useStyles = makeStyles((theme) => ({
  "@global": {
    ".crm-board .react-trello-board": {
      background: "transparent !important",
      padding: "0 !important",
      gap: "14px",
    },
    ".crm-board .react-trello-lane": {
      width: `${BOARD_LANE_WIDTH}px !important`,
      minWidth: `${BOARD_LANE_WIDTH}px !important`,
      maxWidth: `${BOARD_LANE_WIDTH}px !important`,
      borderRadius: "10px !important",
      padding: "0 !important",
      marginRight: "14px !important",
      background: `${theme.palette.background.paper} !important`,
      border: `1px solid ${theme.palette.divider} !important`,
      boxShadow: "none !important",
      overflow: "hidden !important",
    },
    ".crm-board .react-trello-lane header": {
      padding: "12px 14px 10px !important",
      background: "transparent !important",
      borderBottom: `1px solid ${theme.palette.divider} !important`,
    },
    ".crm-board .react-trello-lane .smooth-dnd-container": {
      padding: "12px 8px !important",
      minHeight: "220px !important",
      overflowY: "visible !important",
      overflowX: "hidden !important",
    },
    ".crm-board .react-trello-card": {
      width: `${BOARD_CARD_WIDTH}px !important`,
      minWidth: "0 !important",
      maxWidth: `${BOARD_CARD_WIDTH}px !important`,
      margin: "0 auto 10px !important",
      borderRadius: "8px !important",
      background: `${theme.palette.background.paper} !important`,
      border: `1px solid ${theme.palette.divider} !important`,
      boxShadow: "none !important",
      color: `${theme.palette.text.primary} !important`,
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: "2px",
      },
    },
    ".crm-board .smooth-dnd-ghost": {
      width: `${BOARD_CARD_WIDTH}px !important`,
      maxWidth: `${BOARD_CARD_WIDTH}px !important`,
      opacity: "1 !important",
    },
  },
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
  titleWrap: {
    minWidth: 0,
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    color: theme.palette.primary.main,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  title: {
    fontWeight: 600,
    letterSpacing: 0,
  },
  actionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
      "& > *": {
        flex: "1 1 auto",
      },
    },
  },
  field: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      "& fieldset": {
        borderColor: fade(theme.palette.divider, 0.92),
      },
      "&:hover fieldset": {
        borderColor: fade(theme.palette.primary.main, 0.35),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.12)}`,
      },
    },
  },
  boardSection: {
    borderRadius: 10,
    overflow: "hidden",
    border: `1px solid ${fade(theme.palette.divider, 0.86)}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  boardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${fade(theme.palette.divider, 0.72)}`,
    [theme.breakpoints.down("md")]: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
  boardMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    flexShrink: 0,
    [theme.breakpoints.down("md")]: {
      width: "100%",
      justifyContent: "flex-start",
    },
  },
  boardScroller: {
    width: "100%",
    overflowX: "auto",
    padding: theme.spacing(1.5),
    ...theme.scrollbarStyles,
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderTop: `1px solid ${fade(theme.palette.divider, 0.72)}`,
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
  emptyState: {
    padding: theme.spacing(5, 2),
    textAlign: "center",
  },
  dealCard: {
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.1),
    cursor: "pointer",
    minWidth: 0,
  },
  cardTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  cardTitle: {
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardMeta: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
    minWidth: 0,
    "& svg": {
      fontSize: 16,
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
  },
  cardLineText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  chip: {
    fontWeight: 700,
  },
  tablePaper: {
    borderRadius: 10,
    overflow: "hidden",
    border: `1px solid ${fade(theme.palette.divider, 0.86)}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${fade(theme.palette.divider, 0.72)}`,
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  actionsCell: {
    width: 1,
    whiteSpace: "nowrap",
  },
  colorSwatch: {
    width: 26,
    height: 26,
    borderRadius: 8,
    border: `1px solid ${fade(theme.palette.common.black, 0.18)}`,
    flexShrink: 0,
  },
  dialogPaper: {
    borderRadius: 10,
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  dialogActions: {
    padding: theme.spacing(1.5, 3, 2.5),
    gap: theme.spacing(1),
  },
  formSection: {
    "& + &": {
      borderTop: `1px solid ${fade(theme.palette.divider, 0.72)}`,
      marginTop: theme.spacing(2.5),
      paddingTop: theme.spacing(2.5),
    },
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
  workspaceTabs: {
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${fade(theme.palette.divider, 0.72)}`,
    flexShrink: 0,
  },
  workspaceTabLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  workspaceTabCount: {
    minWidth: 20,
    height: 20,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(0, 0.65),
    borderRadius: 10,
    backgroundColor: fade(theme.palette.text.primary, 0.08),
    color: theme.palette.text.secondary,
    fontSize: "0.68rem",
    fontWeight: 700,
    lineHeight: 1,
  },
  workspaceContent: {
    minHeight: 0,
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("md")]: {
      overflowY: "visible",
    },
  },
  overdueText: {
    color: theme.palette.error.main,
    fontWeight: 700,
  },
  overdueChip: {
    borderColor: fade(theme.palette.error.main, 0.45),
    color: theme.palette.error.main,
  },
  dealStatusOpen: {
    backgroundColor: fade("#0284c7", 0.14),
    color: theme.palette.type === "dark" ? "#7dd3fc" : "#075985",
  },
  dealStatusWon: {
    backgroundColor: fade("#16a34a", 0.14),
    color: theme.palette.type === "dark" ? "#86efac" : "#166534",
  },
  dealStatusLost: {
    backgroundColor: fade(theme.palette.error.main, 0.14),
    color: theme.palette.type === "dark" ? "#fca5a5" : "#991b1b",
  },
  activityStatusPending: {
    backgroundColor: fade("#d97706", 0.14),
    color: theme.palette.type === "dark" ? "#fcd34d" : "#92400e",
  },
  activityStatusDone: {
    backgroundColor: fade("#16a34a", 0.14),
    color: theme.palette.type === "dark" ? "#86efac" : "#166534",
  },
  activityStatusCanceled: {
    backgroundColor: fade(theme.palette.text.primary, 0.1),
    color: theme.palette.text.secondary,
  },
  neutralChip: {
    backgroundColor: fade(theme.palette.text.primary, 0.08),
    color: theme.palette.text.secondary,
  },
  warningChip: {
    backgroundColor: fade("#d97706", 0.14),
    color: theme.palette.type === "dark" ? "#fcd34d" : "#92400e",
  },
}));

const StatusChip = ({ status }) => {
  const classes = useStyles();
  const normalizedStatus = status || "open";
  const statusClass = {
    open: classes.dealStatusOpen,
    won: classes.dealStatusWon,
    lost: classes.dealStatusLost,
  }[normalizedStatus];

  return (
    <Chip
      size="small"
      label={DEAL_STATUS_LABELS[normalizedStatus] || normalizedStatus}
      className={`${classes.chip} ${statusClass || classes.dealStatusOpen}`}
    />
  );
};

const ActivityStatusChip = ({ status }) => {
  const classes = useStyles();
  const normalizedStatus = status || "pending";
  const statusClass = {
    pending: classes.activityStatusPending,
    done: classes.activityStatusDone,
    canceled: classes.activityStatusCanceled,
  }[normalizedStatus];

  return (
    <Chip
      size="small"
      label={ACTIVITY_STATUS_LABELS[normalizedStatus] || normalizedStatus}
      className={`${classes.chip} ${
        statusClass || classes.activityStatusPending
      }`}
    />
  );
};

const DealCard = ({ id, metadata, className, style, onClick }) => {
  const classes = useStyles();
  const deal = metadata?.deal;
  const stages = metadata?.stages || [];

  if (!deal) {
    return null;
  }

  const canMove = deal.status === "open";
  const nextActionOverdue = isDealNextActionOverdue(deal);
  const moveActions = stages
    .filter(
      (stage) =>
        stage.isActive !== false && Number(stage.id) !== Number(deal.stageId),
    )
    .map((stage) => ({
      key: `move-${stage.id}`,
      label: `Mover para ${stage.name}`,
      icon: MoveToInboxIcon,
      disabled: !canMove,
      onClick: () => metadata.onMoveDeal(deal, stage.id),
    }));

  return (
    <div
      data-id={id}
      className={className}
      style={style}
      role="button"
      tabIndex={0}
      aria-label={`Abrir negocio ${deal.title}`}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) {
          return;
        }

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.(event);
        }
      }}
    >
      <div className={classes.dealCard}>
        <div className={classes.cardTop}>
          <Box minWidth={0}>
            <Typography className={classes.cardTitle}>{deal.title}</Typography>
            <Typography className={classes.cardMeta}>
              {deal.contact?.name || "Sem contato vinculado"}
            </Typography>
          </Box>
          <Box onClick={(event) => event.stopPropagation()}>
            <RowActionsMenu
              buttonTitle="Acoes do negocio"
              actions={[
                {
                  key: "details",
                  label: "Ver detalhes",
                  icon: BusinessCenterIcon,
                  onClick: () => metadata.onOpenDeal(deal),
                },
                {
                  key: "edit",
                  label: "Editar negocio",
                  icon: EditIcon,
                  onClick: () => metadata.onEditDeal(deal),
                },
                {
                  key: "conversation",
                  label: "Iniciar conversa",
                  icon: ChatBubbleOutlineIcon,
                  disabled: !deal.contactId,
                  onClick: () => metadata.onOpenConversation(deal),
                },
                ...moveActions,
                {
                  key: "won",
                  label: "Registrar ganho",
                  icon: CheckCircleOutlineIcon,
                  hidden: deal.status === "won",
                  onClick: () => metadata.onOutcome(deal, "won"),
                },
                {
                  key: "lost",
                  label: "Registrar perda",
                  icon: CancelIcon,
                  hidden: deal.status === "lost",
                  onClick: () => metadata.onOutcome(deal, "lost"),
                },
                {
                  key: "reopen",
                  label: "Reabrir negocio",
                  icon: ReplayIcon,
                  hidden: deal.status === "open",
                  onClick: () => metadata.onReopenDeal(deal),
                },
                {
                  key: "delete",
                  label: "Excluir negocio",
                  icon: DeleteOutlineIcon,
                  onClick: () => metadata.onDeleteDeal(deal),
                },
              ]}
            />
          </Box>
        </div>

        <div className={classes.cardLine}>
          <AttachMoneyIcon />
          <span className={classes.cardLineText}>
            {formatCurrency(deal.amount, deal.currency)}
          </span>
        </div>

        <div className={classes.cardLine}>
          <PersonIcon />
          <span className={classes.cardLineText}>
            {deal.user?.name || "Sem responsavel"}
          </span>
        </div>

        <div
          className={`${classes.cardLine} ${
            nextActionOverdue ? classes.overdueText : ""
          }`}
        >
          <EventAvailableIcon />
          <span className={classes.cardLineText}>
            {deal.nextActionNote
              ? `${deal.nextActionNote} - ${formatDateTime(deal.nextActionAt)}${
                  nextActionOverdue ? " - Atrasada" : ""
                }`
              : "Sem proxima acao"}
          </span>
        </div>

        <div className={classes.cardFooter}>
          <StatusChip status={deal.status} />
          {deal.source ? <Chip size="small" label={deal.source} /> : null}
        </div>
      </div>
    </div>
  );
};

const Crm = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { user, socket } = useContext(AuthContext);
  const canManagePipeline = canManageCrmPipeline(user);

  const [pipelines, setPipelines] = useState([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState("");
  const [deals, setDeals] = useState([]);
  const [dealsPage, setDealsPage] = useState(0);
  const [dealsCount, setDealsCount] = useState(0);
  const [dealsHasMore, setDealsHasMore] = useState(false);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [loadingMoreDeals, setLoadingMoreDeals] = useState(false);
  const [saving, setSaving] = useState(false);
  const [movingDealId, setMovingDealId] = useState(null);
  const [startingConversationDealId, setStartingConversationDealId] =
    useState(null);
  const [activeDealConversation, setActiveDealConversation] = useState(null);
  const [crmAvailability, setCrmAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const dealsRequestIdRef = useRef(0);
  const crmRealtimeTimerRef = useRef(null);
  const pendingCrmRefreshRef = useRef({
    reloadPipelines: false,
    reloadDeals: false,
    reloadDetail: false,
    closeDetail: false,
    detailDealIdToReload: null,
    detailDealIdToClose: null,
  });
  const crmRealtimeContextRef = useRef({});

  const [filters, setFilters] = useState(EMPTY_DEAL_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_DEAL_FILTERS);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [users, setUsers] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState("");

  const [pipelineManagerOpen, setPipelineManagerOpen] = useState(false);
  const [pipelineFormOpen, setPipelineFormOpen] = useState(false);
  const [pipelineForm, setPipelineForm] = useState(EMPTY_PIPELINE_FORM);

  const [stageManagerOpen, setStageManagerOpen] = useState(false);
  const [stageFormOpen, setStageFormOpen] = useState(false);
  const [stageForm, setStageForm] = useState(EMPTY_STAGE_FORM);
  const [dealFormOpen, setDealFormOpen] = useState(false);
  const [dealForm, setDealForm] = useState(EMPTY_DEAL_FORM);
  const [dealFormError, setDealFormError] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTab, setDetailTab] = useState("activities");
  const [detailDeal, setDetailDeal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [conversationCount, setConversationCount] = useState(0);
  const [detailEditing, setDetailEditing] = useState(false);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
  const [detailOutcome, setDetailOutcome] = useState({
    status: null,
    lossReason: "",
    error: "",
  });

  const [activityFormOpen, setActivityFormOpen] = useState(false);
  const [activityForm, setActivityForm] = useState(EMPTY_ACTIVITY_FORM);
  const [activityDealId, setActivityDealId] = useState(null);

  const [outcomeDialog, setOutcomeDialog] = useState({
    open: false,
    deal: null,
    status: "won",
    lossReason: "",
  });

  const [confirmState, setConfirmState] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const selectedPipeline = useMemo(
    () =>
      pipelines.find(
        (pipeline) => Number(pipeline.id) === Number(selectedPipelineId),
      ),
    [pipelines, selectedPipelineId],
  );
  const stages = useMemo(
    () => normalizePipelineStages(selectedPipeline),
    [selectedPipeline],
  );
  const activeStages = useMemo(() => getActiveStages(stages), [stages]);
  const summary = useMemo(() => summarizeDeals(deals), [deals]);
  const overdueActivitiesCount = useMemo(
    () =>
      (detailDeal?.activities || []).filter((activity) =>
        isActivityOverdue(activity),
      ).length,
    [detailDeal],
  );
  const detailNextActionOverdue = isDealNextActionOverdue(detailDeal);
  const detailHistoryCount = useMemo(() => {
    if (!detailDeal) {
      return 0;
    }

    return (
      (detailDeal.stageHistories || []).length +
      (detailDeal.createdAt ? 1 : 0) +
      (detailDeal.closedAt && ["won", "lost"].includes(detailDeal.status)
        ? 1
        : 0)
    );
  }, [detailDeal]);

  const loadCrmAvailability = useCallback(async () => {
    setAvailabilityLoading(true);
    try {
      const availability = await crmService.getAvailability();
      const normalizedAvailability = {
        enabled: availability?.enabled !== false,
      };
      setCrmAvailability(normalizedAvailability);
      return normalizedAvailability;
    } catch (error) {
      toastError(error);
      const unavailable = { enabled: false };
      setCrmAvailability(unavailable);
      return unavailable;
    } finally {
      setAvailabilityLoading(false);
    }
  }, []);

  const loadPipelines = useCallback(async () => {
    setLoadingPipelines(true);
    try {
      const data = await crmService.listPipelines({ includeInactive: true });
      const nextPipelines = Array.isArray(data) ? data : [];

      setPipelines(nextPipelines);
      setSelectedPipelineId((currentPipelineId) => {
        if (
          currentPipelineId &&
          nextPipelines.some(
            (pipeline) => Number(pipeline.id) === Number(currentPipelineId),
          )
        ) {
          return currentPipelineId;
        }

        const defaultPipeline =
          nextPipelines.find(
            (pipeline) => pipeline.isDefault && pipeline.isActive !== false,
          ) ||
          nextPipelines.find((pipeline) => pipeline.isActive !== false) ||
          nextPipelines[0];

        return defaultPipeline?.id || "";
      });
    } catch (error) {
      toastError(error);
      setPipelines([]);
      setSelectedPipelineId("");
    } finally {
      setLoadingPipelines(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await crmService.listUsers();
      setUsers((data || []).map((user) => ({ id: user.id, name: user.name })));
    } catch (error) {
      toastError(error);
    }
  }, []);

  const loadDeals = useCallback(
    async ({ pageNumber = 1, append = false, requestId } = {}) => {
      const activeRequestId = requestId ?? dealsRequestIdRef.current + 1;

      if (requestId === undefined) {
        dealsRequestIdRef.current = activeRequestId;
      }

      if (activeRequestId !== dealsRequestIdRef.current) {
        return;
      }

      if (!selectedPipelineId) {
        setDeals([]);
        setDealsPage(0);
        setDealsCount(0);
        setDealsHasMore(false);
        setLoadingDeals(false);
        setLoadingMoreDeals(false);
        return;
      }

      if (append) {
        setLoadingMoreDeals(true);
      } else {
        setLoadingDeals(true);
      }

      try {
        const data = await crmService.listDeals({
          pipelineId: selectedPipelineId,
          pageNumber,
          pageSize: DEALS_PAGE_SIZE,
          searchParam: filters.searchParam || undefined,
          status: filters.status || undefined,
          userId: filters.userId || undefined,
          contactId: filters.contact?.id || undefined,
          dateStart: toDateStartBoundary(filters.dateStart),
          dateEnd: toDateEndBoundary(filters.dateEnd),
        });

        if (activeRequestId !== dealsRequestIdRef.current) {
          return;
        }

        const pageDeals = Array.isArray(data?.deals) ? data.deals : [];
        const parsedCount = Number(data?.count);

        setDeals((currentDeals) =>
          append ? mergeDealsById(currentDeals, pageDeals) : pageDeals,
        );
        setDealsPage(pageNumber);
        setDealsCount((currentCount) =>
          Number.isFinite(parsedCount)
            ? parsedCount
            : append
              ? Math.max(
                  currentCount,
                  (pageNumber - 1) * DEALS_PAGE_SIZE + pageDeals.length,
                )
              : pageDeals.length,
        );
        setDealsHasMore(Boolean(data?.hasMore));
      } catch (error) {
        if (activeRequestId !== dealsRequestIdRef.current) {
          return;
        }

        toastError(error);

        if (!append) {
          setDeals([]);
          setDealsPage(0);
          setDealsCount(0);
          setDealsHasMore(false);
        }
      } finally {
        if (activeRequestId === dealsRequestIdRef.current) {
          setLoadingDeals(false);
          setLoadingMoreDeals(false);
        }
      }
    },
    [
      filters.contact,
      filters.dateEnd,
      filters.dateStart,
      filters.searchParam,
      filters.status,
      filters.userId,
      selectedPipelineId,
    ],
  );

  useEffect(() => {
    loadCrmAvailability();
  }, [loadCrmAvailability]);

  useEffect(() => {
    if (crmAvailability?.enabled) {
      loadPipelines();
      loadUsers();
    }
  }, [crmAvailability?.enabled, loadPipelines, loadUsers]);

  useEffect(() => {
    const requestId = dealsRequestIdRef.current + 1;
    dealsRequestIdRef.current = requestId;
    setDeals([]);
    setDealsPage(0);
    setDealsCount(0);
    setDealsHasMore(false);
    setLoadingDeals(true);
    setLoadingMoreDeals(false);

    const timer = setTimeout(() => {
      loadDeals({ requestId });
    }, 350);

    return () => {
      clearTimeout(timer);

      if (dealsRequestIdRef.current === requestId) {
        dealsRequestIdRef.current += 1;
      }
    };
  }, [loadDeals]);

  useEffect(() => {
    if (!crmAvailability?.enabled) {
      return undefined;
    }

    let isMounted = true;

    const timer = setTimeout(async () => {
      setContactsLoading(true);
      try {
        const contacts = await crmService.searchContacts(contactSearch);
        if (isMounted) {
          setContactOptions(contacts);
        }
      } catch (error) {
        if (isMounted) {
          toastError(error);
        }
      } finally {
        if (isMounted) {
          setContactsLoading(false);
        }
      }
    }, 350);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [contactSearch, crmAvailability?.enabled]);

  const refreshDetailDeal = useCallback(async (dealId) => {
    if (!dealId) {
      return null;
    }

    setDetailLoading(true);
    try {
      const deal = await crmService.getDeal(dealId);
      setDetailDeal(deal);
      return deal;
    } catch (error) {
      toastError(error);
      return null;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    crmRealtimeContextRef.current = {
      detailDealId: detailOpen ? detailDeal?.id : null,
      detailOpen,
      loadDeals,
      loadPipelines,
      refreshDetailDeal,
      selectedPipelineId,
    };
  }, [
    detailDeal?.id,
    detailOpen,
    loadDeals,
    loadPipelines,
    refreshDetailDeal,
    selectedPipelineId,
  ]);

  useEffect(() => {
    const companyId = user?.companyId;

    if (!companyId || !socket?.on || !socket?.off) {
      return undefined;
    }

    const eventName = `company-${companyId}-crm`;
    const onCrmEvent = (event) => {
      const currentContext = crmRealtimeContextRef.current;
      const refreshPlan = getCrmRealtimeRefreshPlan({
        event,
        companyId,
        selectedPipelineId: currentContext.selectedPipelineId,
        detailDealId: currentContext.detailDealId,
      });

      if (refreshPlan.ignore) {
        return;
      }

      pendingCrmRefreshRef.current = {
        reloadPipelines:
          pendingCrmRefreshRef.current.reloadPipelines ||
          refreshPlan.reloadPipelines,
        reloadDeals:
          pendingCrmRefreshRef.current.reloadDeals || refreshPlan.reloadDeals,
        reloadDetail:
          pendingCrmRefreshRef.current.reloadDetail || refreshPlan.reloadDetail,
        closeDetail:
          pendingCrmRefreshRef.current.closeDetail || refreshPlan.closeDetail,
        detailDealIdToReload: refreshPlan.reloadDetail
          ? currentContext.detailDealId
          : pendingCrmRefreshRef.current.detailDealIdToReload,
        detailDealIdToClose: refreshPlan.closeDetail
          ? currentContext.detailDealId
          : pendingCrmRefreshRef.current.detailDealIdToClose,
      };

      clearTimeout(crmRealtimeTimerRef.current);
      crmRealtimeTimerRef.current = setTimeout(async () => {
        const pendingRefresh = pendingCrmRefreshRef.current;
        pendingCrmRefreshRef.current = {
          reloadPipelines: false,
          reloadDeals: false,
          reloadDetail: false,
          closeDetail: false,
          detailDealIdToReload: null,
          detailDealIdToClose: null,
        };

        const latestContext = crmRealtimeContextRef.current;
        const shouldCloseDetail =
          pendingRefresh.closeDetail &&
          Number(pendingRefresh.detailDealIdToClose) ===
            Number(latestContext.detailDealId);

        if (shouldCloseDetail) {
          setDetailOpen(false);
          setDetailDeal(null);
        }

        if (pendingRefresh.reloadPipelines) {
          await latestContext.loadPipelines?.();
        }

        if (pendingRefresh.reloadDeals) {
          await latestContext.loadDeals?.();
        }

        if (
          !shouldCloseDetail &&
          pendingRefresh.reloadDetail &&
          latestContext.detailOpen &&
          latestContext.detailDealId &&
          Number(pendingRefresh.detailDealIdToReload) ===
            Number(latestContext.detailDealId)
        ) {
          await latestContext.refreshDetailDeal?.(latestContext.detailDealId);
        }
      }, 150);
    };

    socket.on(eventName, onCrmEvent);

    return () => {
      socket.off(eventName, onCrmEvent);
      clearTimeout(crmRealtimeTimerRef.current);
      pendingCrmRefreshRef.current = {
        reloadPipelines: false,
        reloadDeals: false,
        reloadDetail: false,
        closeDetail: false,
        detailDealIdToReload: null,
        detailDealIdToClose: null,
      };
    };
  }, [socket, user?.companyId]);

  const handleRefresh = async () => {
    const availability = await loadCrmAvailability();
    if (!availability?.enabled) {
      return;
    }
    await loadPipelines();
    await loadDeals();
  };

  const handleLoadMoreDeals = () => {
    if (!dealsHasMore || loadingDeals || loadingMoreDeals) {
      return;
    }

    loadDeals({ pageNumber: dealsPage + 1, append: true });
  };

  const handleOpenDealDetail = useCallback(
    (deal) => {
      setActiveDealConversation(null);
      setDetailEditing(false);
      setDetailOutcome({ status: null, lossReason: "", error: "" });
      setStatusMenuAnchorEl(null);
      setConversationCount(0);
      setDetailOpen(true);
      setDetailTab("activities");
      setDetailDeal(deal);
      refreshDetailDeal(deal.id);
    },
    [refreshDetailDeal],
  );

  useEffect(() => {
    const dealId = new URLSearchParams(location.search).get("dealId");
    if (!dealId || !crmAvailability?.enabled) return;

    let active = true;
    crmService
      .getDeal(dealId)
      .then((deal) => {
        if (!active) return;
        setActiveDealConversation(null);
        setDetailEditing(false);
        setDetailOutcome({ status: null, lossReason: "", error: "" });
        setStatusMenuAnchorEl(null);
        setConversationCount(0);
        setDetailDeal(deal);
        setDetailTab("activities");
        setDetailOpen(true);
      })
      .catch(toastError);

    return () => {
      active = false;
    };
  }, [crmAvailability?.enabled, location.search]);

  const handleCloseDealDetail = () => {
    setDetailOpen(false);
    setDetailDeal(null);
    setActiveDealConversation(null);
    setDetailEditing(false);
    setDetailOutcome({ status: null, lossReason: "", error: "" });
    setStatusMenuAnchorEl(null);
    setConversationCount(0);
  };

  const openStandaloneFromDetail = (action) => {
    if (!detailDeal) {
      return;
    }

    const deal = detailDeal;
    handleCloseDealDetail();
    action(deal);
  };

  const handleActiveConversationChange = useCallback(({ dealId, ticket }) => {
    setActiveDealConversation(ticket ? { dealId, ticket } : null);
  }, []);

  const handleOpenConversation = useCallback(
    async (deal) => {
      if (!deal?.contactId || !deal?.contact) {
        toast.warn(
          "Vincule um contato ao negocio antes de iniciar a conversa.",
        );
        return;
      }

      if (
        Number(activeDealConversation?.dealId) === Number(deal.id) &&
        activeDealConversation?.ticket?.uuid
      ) {
        history.push(`/tickets/${activeDealConversation.ticket.uuid}`);
        return;
      }

      setStartingConversationDealId(deal.id);
      try {
        const result = await crmService.startDealConversation(deal.id);

        if (!result?.ticket?.uuid) {
          toast.error("Nao foi possivel iniciar a conversa para este contato.");
          return;
        }

        toast.success(
          result.reused
            ? "Conversa ativa aberta e vinculada ao negocio."
            : "Nova conversa iniciada e vinculada ao negocio.",
        );
        history.push(`/tickets/${result.ticket.uuid}`);
      } catch (error) {
        const code = error?.response?.data?.error;
        if (code === "ERR_CRM_DEAL_CONTACT_REQUIRED") {
          toast.warn(
            "Vincule um contato ao negocio antes de iniciar a conversa.",
          );
        } else if (code === "ERR_CRM_TICKET_PERMISSION_DENIED") {
          toast.error(
            "A conversa ativa deste contato pertence a outro atendimento.",
          );
        } else {
          toastError(error);
        }
      } finally {
        setStartingConversationDealId(null);
      }
    },
    [activeDealConversation, history],
  );

  const handleOpenCreatePipeline = () => {
    const maxOrder = pipelines.reduce(
      (max, pipeline) => Math.max(max, Number(pipeline.order || 0)),
      0,
    );

    setPipelineForm({
      ...EMPTY_PIPELINE_FORM,
      order: maxOrder + 1,
    });
    setPipelineFormOpen(true);
  };

  const handleOpenEditPipeline = (pipeline) => {
    setPipelineForm({
      id: pipeline.id,
      name: pipeline.name || "",
      description: pipeline.description || "",
      order: Number(pipeline.order || 0),
      isActive: pipeline.isActive !== false,
      isDefault: pipeline.isDefault === true,
    });
    setPipelineFormOpen(true);
  };

  const handleSubmitPipeline = async (event) => {
    event.preventDefault();

    if (!pipelineForm.name.trim()) {
      toast.error("Informe o nome do funil.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: pipelineForm.name.trim(),
        description: pipelineForm.description || null,
        order: Number(pipelineForm.order || 0),
        isActive: pipelineForm.isActive,
        isDefault: pipelineForm.isDefault,
      };

      if (pipelineForm.id) {
        await crmService.updatePipeline(pipelineForm.id, payload);
        toast.success("Funil atualizado com sucesso.");
      } else {
        await crmService.createPipeline(payload);
        toast.success("Funil criado com sucesso.");
      }

      setPipelineFormOpen(false);
      await loadPipelines();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const requestDeletePipeline = (pipeline) => {
    setConfirmState({
      open: true,
      title: "Excluir funil",
      message: `Deseja excluir o funil ${pipeline.name}? Funis com negocios vinculados nao podem ser excluidos.`,
      onConfirm: async () => {
        setSaving(true);
        try {
          await crmService.deletePipeline(pipeline.id);
          toast.success("Funil excluido com sucesso.");
          await loadPipelines();
        } catch (error) {
          toastError(error);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleOpenCreateStage = () => {
    if (!selectedPipeline) {
      return;
    }

    const maxOrder = stages.reduce(
      (max, stage) => Math.max(max, Number(stage.order || 0)),
      0,
    );

    setStageForm({
      ...EMPTY_STAGE_FORM,
      order: maxOrder + 1,
    });
    setStageFormOpen(true);
  };

  const handleOpenEditStage = (stage) => {
    setStageForm({
      id: stage.id,
      name: stage.name || "",
      color: stage.color || "#2f80ed",
      order: Number(stage.order || 0),
      isActive: stage.isActive !== false,
    });
    setStageFormOpen(true);
  };

  const handleSubmitStage = async (event) => {
    event.preventDefault();

    if (!selectedPipeline) {
      toast.error("Selecione um funil antes de criar etapas.");
      return;
    }

    if (!stageForm.name.trim()) {
      toast.error("Informe o nome da etapa.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: stageForm.name.trim(),
        color: stageForm.color || null,
        order: Number(stageForm.order || 0),
        isActive: stageForm.isActive,
      };

      if (stageForm.id) {
        await crmService.updateStage(stageForm.id, payload);
        toast.success("Etapa atualizada com sucesso.");
      } else {
        await crmService.createStage(selectedPipeline.id, payload);
        toast.success("Etapa criada com sucesso.");
      }

      setStageFormOpen(false);
      await loadPipelines();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStageActive = async (stage) => {
    setSaving(true);
    try {
      await crmService.updateStage(stage.id, {
        isActive: stage.isActive === false,
      });
      toast.success(
        stage.isActive === false ? "Etapa ativada." : "Etapa desativada.",
      );
      await loadPipelines();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReorderStage = async (stage, direction) => {
    const sortedStages = normalizePipelineStages(stages);
    const stageIndex = sortedStages.findIndex(
      (item) => Number(item.id) === Number(stage.id),
    );
    const targetStage = sortedStages[stageIndex + direction];

    if (!targetStage) {
      return;
    }

    const currentOrder = Number(stage.order || 0);
    const targetOrder = Number(targetStage.order || 0);
    const temporaryOrder =
      sortedStages.reduce(
        (max, item) => Math.max(max, Number(item.order || 0)),
        0,
      ) +
      1000 +
      Date.now();

    setSaving(true);
    try {
      await crmService.updateStage(stage.id, { order: temporaryOrder });
      await crmService.updateStage(targetStage.id, { order: currentOrder });
      await crmService.updateStage(stage.id, { order: targetOrder });
      toast.success("Ordem da etapa atualizada.");
      await loadPipelines();
    } catch (error) {
      toastError(error);
      await loadPipelines();
    } finally {
      setSaving(false);
    }
  };

  const requestDeleteStage = (stage) => {
    setConfirmState({
      open: true,
      title: "Excluir etapa",
      message: `Deseja excluir a etapa ${stage.name}? Etapas com negocios vinculados nao podem ser excluidas.`,
      onConfirm: async () => {
        setSaving(true);
        try {
          await crmService.deleteStage(stage.id);
          toast.success("Etapa excluida com sucesso.");
          await loadPipelines();
          await loadDeals();
        } catch (error) {
          toastError(error);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleOpenCreateDeal = () => {
    if (!selectedPipeline) {
      toast.error("Crie ou selecione um funil antes de cadastrar negocios.");
      return;
    }

    if (activeStages.length === 0) {
      toast.error("O funil precisa ter pelo menos uma etapa ativa.");
      return;
    }

    setDealForm({
      ...EMPTY_DEAL_FORM,
      pipelineId: selectedPipeline.id,
      stageId: getDefaultStageId(selectedPipeline),
    });
    setDealFormError("");
    setDealFormOpen(true);
  };

  const handleOpenEditDeal = useCallback((deal) => {
    setDealForm(getDealFormValues(deal));
    setDealFormError("");
    setDealFormOpen(true);
  }, []);

  const handleStartInlineEdit = () => {
    if (!detailDeal) {
      return;
    }

    setDetailOutcome({ status: null, lossReason: "", error: "" });
    setStatusMenuAnchorEl(null);
    setDealForm(getDealFormValues(detailDeal));
    setDealFormError("");
    setDetailEditing(true);
  };

  const handleCancelInlineEdit = () => {
    setDetailEditing(false);
    setDealFormError("");
  };

  const handleInlineDealFieldChange = (field, value) => {
    setDealForm((current) => ({ ...current, [field]: value }));
    setDealFormError("");
  };

  const handleSubmitDeal = async (event) => {
    event.preventDefault();
    setDealFormError("");

    if (!dealForm.title.trim()) {
      const message = "Informe o titulo do negocio.";
      setDealFormError(message);
      toast.error(message);
      return;
    }

    const amount = toNumberOrNull(dealForm.amount);

    if (amount !== null && amount < 0) {
      const message = "O valor do negocio nao pode ser negativo.";
      setDealFormError(message);
      toast.error(message);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: dealForm.title.trim(),
        description: dealForm.description || null,
        amount: amount ?? 0,
        currency: (dealForm.currency || "BRL").toUpperCase(),
        source: dealForm.source || null,
        expectedCloseAt: fromDateInput(dealForm.expectedCloseAt),
        contactId: dealForm.contact?.id || null,
        userId: dealForm.userId || null,
      };

      if (dealForm.id) {
        const updatedDeal = await crmService.updateDeal(dealForm.id, payload);
        toast.success("Negocio atualizado com sucesso.");
        if (detailOpen && Number(detailDeal?.id) === Number(dealForm.id)) {
          setDetailDeal(updatedDeal);
          refreshDetailDeal(dealForm.id);
        }
      } else {
        await crmService.createDeal({
          ...payload,
          pipelineId: dealForm.pipelineId,
          stageId: dealForm.stageId || undefined,
        });
        toast.success("Negocio criado com sucesso.");
      }

      setDealFormOpen(false);
      setDetailEditing(false);
      setDealFormError("");
      await loadDeals();
    } catch (error) {
      if (detailEditing) {
        setDealFormError("Nao foi possivel salvar as alteracoes.");
      }
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const requestDeleteDeal = useCallback(
    (deal) => {
      setConfirmState({
        open: true,
        title: "Excluir negocio",
        message: `Deseja excluir o negocio ${deal.title}? Esta acao nao pode ser revertida.`,
        onConfirm: async () => {
          setSaving(true);
          try {
            await crmService.deleteDeal(deal.id);
            toast.success("Negocio excluido com sucesso.");
            if (detailOpen && Number(detailDeal?.id) === Number(deal.id)) {
              handleCloseDealDetail();
            }
            await loadDeals();
          } catch (error) {
            toastError(error);
          } finally {
            setSaving(false);
          }
        },
      });
    },
    [detailDeal, detailOpen, loadDeals],
  );

  const handleMoveDealToStage = useCallback(
    async (deal, stageId) => {
      const targetStage = stages.find(
        (stage) => Number(stage.id) === Number(stageId),
      );

      if (!targetStage) {
        toast.error("Etapa de destino nao encontrada.");
        await loadDeals();
        return;
      }

      if (targetStage.isActive === false) {
        toast.error("Nao e possivel mover negocios para uma etapa inativa.");
        await loadDeals();
        return;
      }

      if (deal.status !== "open") {
        toast.error("Negocios fechados nao podem mudar de etapa.");
        await loadDeals();
        return;
      }

      setMovingDealId(deal.id);
      try {
        const movedDeal = await crmService.moveDealStage(deal.id, {
          stageId: targetStage.id,
        });
        toast.success("Negocio movido com sucesso.");
        if (detailOpen && Number(detailDeal?.id) === Number(deal.id)) {
          setDetailDeal(movedDeal);
          refreshDetailDeal(deal.id);
        }
        await loadDeals();
      } catch (error) {
        toastError(error);
        await loadDeals();
      } finally {
        setMovingDealId(null);
      }
    },
    [detailDeal, detailOpen, loadDeals, refreshDetailDeal, stages],
  );

  const handleBoardCardMove = async (fromLaneId, toLaneId, cardId) => {
    if (fromLaneId === toLaneId) {
      return;
    }

    const deal = deals.find((item) => Number(item.id) === Number(cardId));

    if (!deal) {
      await loadDeals();
      return;
    }

    await handleMoveDealToStage(deal, toLaneId);
  };

  const openOutcomeDialog = useCallback((deal, status) => {
    setOutcomeDialog({
      open: true,
      deal,
      status,
      lossReason: "",
    });
  }, []);

  const handleSubmitOutcome = async (event) => {
    event.preventDefault();

    if (outcomeDialog.status === "lost" && !outcomeDialog.lossReason.trim()) {
      toast.error("Informe o motivo da perda.");
      return;
    }

    setSaving(true);
    try {
      const updatedDeal = await crmService.registerDealOutcome(
        outcomeDialog.deal.id,
        {
          status: outcomeDialog.status,
          lossReason:
            outcomeDialog.status === "lost"
              ? outcomeDialog.lossReason.trim()
              : undefined,
        },
      );
      toast.success(
        outcomeDialog.status === "won"
          ? "Ganho registrado com sucesso."
          : "Perda registrada com sucesso.",
      );
      setOutcomeDialog({
        open: false,
        deal: null,
        status: "won",
        lossReason: "",
      });
      if (detailOpen && Number(detailDeal?.id) === Number(updatedDeal.id)) {
        setDetailDeal(updatedDeal);
        refreshDetailDeal(updatedDeal.id);
      }
      await loadDeals();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleStartInlineOutcome = (status) => {
    setStatusMenuAnchorEl(null);
    setDetailEditing(false);
    setDealFormError("");
    setDetailOutcome({ status, lossReason: "", error: "" });
  };

  const handleCancelInlineOutcome = () => {
    setDetailOutcome({ status: null, lossReason: "", error: "" });
  };

  const handleSubmitInlineOutcome = async (event) => {
    event.preventDefault();

    if (!detailDeal || !detailOutcome.status) {
      return;
    }

    if (
      detailOutcome.status === "lost" &&
      !detailOutcome.lossReason.trim()
    ) {
      setDetailOutcome((current) => ({
        ...current,
        error: "Informe o motivo da perda.",
      }));
      return;
    }

    setSaving(true);
    setDetailOutcome((current) => ({ ...current, error: "" }));

    try {
      const updatedDeal = await crmService.registerDealOutcome(detailDeal.id, {
        status: detailOutcome.status,
        lossReason:
          detailOutcome.status === "lost"
            ? detailOutcome.lossReason.trim()
            : undefined,
      });

      toast.success(
        detailOutcome.status === "won"
          ? "Ganho registrado com sucesso."
          : detailOutcome.status === "lost"
            ? "Perda registrada com sucesso."
            : "Negocio reaberto com sucesso.",
      );
      setDetailDeal(updatedDeal);
      setDetailOutcome({ status: null, lossReason: "", error: "" });
      refreshDetailDeal(updatedDeal.id);
      await loadDeals();
    } catch (error) {
      setDetailOutcome((current) => ({
        ...current,
        error: "Nao foi possivel alterar o status do negocio.",
      }));
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const requestReopenDeal = useCallback(
    (deal) => {
      setConfirmState({
        open: true,
        title: "Reabrir negocio",
        message: `Deseja reabrir o negocio ${deal.title}? Os dados do fechamento anterior serao limpos.`,
        onConfirm: async () => {
          setSaving(true);
          try {
            const reopenedDeal = await crmService.registerDealOutcome(deal.id, {
              status: "open",
            });
            toast.success("Negocio reaberto com sucesso.");
            setConfirmState((current) => ({
              ...current,
              open: false,
              onConfirm: null,
            }));

            if (
              detailOpen &&
              Number(detailDeal?.id) === Number(reopenedDeal.id)
            ) {
              setDetailDeal(reopenedDeal);
              refreshDetailDeal(reopenedDeal.id);
            }

            await loadDeals();
          } catch (error) {
            toastError(error);
          } finally {
            setSaving(false);
          }
        },
      });
    },
    [detailDeal, detailOpen, loadDeals, refreshDetailDeal],
  );

  const handleOpenCreateActivity = (deal) => {
    setActivityDealId(deal.id);
    setActivityForm({
      ...EMPTY_ACTIVITY_FORM,
      userId: deal.userId || "",
    });
    setActivityFormOpen(true);
  };

  const handleOpenEditActivity = (activity) => {
    setActivityDealId(activity.dealId);
    setActivityForm({
      id: activity.id,
      type: activity.type || "task",
      title: activity.title || "",
      description: activity.description || "",
      dueAt: toDateTimeInput(activity.dueAt),
      status: activity.status || "pending",
      isNextAction: activity.isNextAction === true,
      userId: activity.userId || "",
    });
    setActivityFormOpen(true);
  };

  const afterActivityMutation = async (dealId) => {
    await refreshDetailDeal(dealId);
    await loadDeals();
  };

  const handleSubmitActivity = async (event) => {
    event.preventDefault();

    if (!activityForm.title.trim()) {
      toast.error("Informe o titulo da atividade.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        type: activityForm.type || "task",
        title: activityForm.title.trim(),
        description: activityForm.description || null,
        dueAt: fromDateTimeInput(activityForm.dueAt),
        status: activityForm.status,
        isNextAction: activityForm.isNextAction,
        userId: activityForm.userId || null,
      };

      if (activityForm.id) {
        await crmService.updateActivity(activityForm.id, payload);
        toast.success("Atividade atualizada com sucesso.");
      } else {
        await crmService.createActivity(activityDealId, payload);
        toast.success("Atividade criada com sucesso.");
      }

      setActivityFormOpen(false);
      await afterActivityMutation(activityDealId);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateActivityStatus = async (activity, status) => {
    setSaving(true);
    try {
      await crmService.updateActivity(activity.id, { status });
      toast.success("Atividade atualizada.");
      await afterActivityMutation(activity.dealId);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkNextAction = async (activity) => {
    setSaving(true);
    try {
      await crmService.updateActivity(activity.id, { isNextAction: true });
      toast.success("Proxima acao atualizada.");
      await afterActivityMutation(activity.dealId);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const requestDeleteActivity = (activity) => {
    setConfirmState({
      open: true,
      title: "Excluir atividade",
      message: `Deseja excluir a atividade ${activity.title}?`,
      onConfirm: async () => {
        setSaving(true);
        try {
          await crmService.deleteActivity(activity.id);
          toast.success("Atividade excluida com sucesso.");
          await afterActivityMutation(activity.dealId);
        } catch (error) {
          toastError(error);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const boardData = useMemo(
    () =>
      buildCrmBoardData({
        stages,
        deals,
        makeCardMetadata: (deal, stage) => ({
          deal,
          stage,
          stages,
          onOpenDeal: handleOpenDealDetail,
          onEditDeal: handleOpenEditDeal,
          onOpenConversation: handleOpenConversation,
          onMoveDeal: handleMoveDealToStage,
          onOutcome: openOutcomeDialog,
          onReopenDeal: requestReopenDeal,
          onDeleteDeal: requestDeleteDeal,
        }),
      }),
    [
      deals,
      handleMoveDealToStage,
      handleOpenDealDetail,
      handleOpenEditDeal,
      handleOpenConversation,
      openOutcomeDialog,
      requestDeleteDeal,
      requestReopenDeal,
      stages,
    ],
  );

  const handleOpenAdvancedFilters = (event) => {
    setDraftFilters({
      ...filters,
      contact: filters.contact ? { ...filters.contact } : null,
    });
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseAdvancedFilters = () => {
    const anchor = filterAnchorEl;
    setFilterAnchorEl(null);
    window.requestAnimationFrame(() => anchor?.focus());
  };

  const handleClearDraftFilters = () => {
    setDraftFilters((current) => ({
      ...current,
      status: "",
      userId: "",
      contact: null,
      dateStart: "",
      dateEnd: "",
    }));
  };

  const handleApplyAdvancedFilters = () => {
    setFilters((current) => ({
      ...current,
      status: draftFilters.status,
      userId: draftFilters.userId,
      contact: draftFilters.contact,
      dateStart: draftFilters.dateStart,
      dateEnd: draftFilters.dateEnd,
    }));
    setDealsPage(0);
    handleCloseAdvancedFilters();
  };

  const handleRemoveAdvancedFilter = (key) => {
    setFilters((current) => ({
      ...current,
      [key]: key === "contact" ? null : "",
    }));
    setDealsPage(0);
  };

  const handleClearAppliedFilters = () => {
    setFilters((current) => ({
      ...current,
      status: "",
      userId: "",
      contact: null,
      dateStart: "",
      dateEnd: "",
    }));
    setDraftFilters((current) => ({
      ...current,
      status: "",
      userId: "",
      contact: null,
      dateStart: "",
      dateEnd: "",
    }));
    setDealsPage(0);
  };

  const activeAdvancedFiltersCount = [
    filters.status,
    filters.userId,
    filters.contact,
    filters.dateStart,
    filters.dateEnd,
  ].filter(Boolean).length;

  const advancedFiltersOpen = Boolean(filterAnchorEl);

  const appliedFilterChips = [
    filters.status
      ? {
          key: "status",
          label: `Status: ${DEAL_STATUS_LABELS[filters.status] || filters.status}`,
        }
      : null,
    filters.userId
      ? {
          key: "userId",
          label: `Responsavel: ${
            users.find((item) => Number(item.id) === Number(filters.userId))
              ?.name || filters.userId
          }`,
        }
      : null,
    filters.contact
      ? { key: "contact", label: `Contato: ${filters.contact.name}` }
      : null,
    filters.dateStart
      ? {
          key: "dateStart",
          label: `Desde: ${formatFilterDate(filters.dateStart)}`,
        }
      : null,
    filters.dateEnd
      ? { key: "dateEnd", label: `Ate: ${formatFilterDate(filters.dateEnd)}` }
      : null,
  ].filter(Boolean);

  const renderStageName = (stage) => (
    <Box display="flex" alignItems="center" gridGap={10} minWidth={0}>
      <span
        className={classes.colorSwatch}
        style={{ backgroundColor: stage.color || "#94a3b8" }}
      />
      <Box minWidth={0}>
        <Typography style={{ fontWeight: 600 }}>{stage.name}</Typography>
        <Typography variant="body2" color="textSecondary">
          Ordem {stage.order ?? 0}
        </Typography>
      </Box>
    </Box>
  );

  const renderActivityActions = (activity, closeDetail = false) => (
    <RowActionsMenu
      buttonTitle="Acoes da atividade"
      disabled={saving}
      actions={[
        {
          key: "edit",
          label: "Editar atividade",
          icon: EditIcon,
          onClick: () =>
            closeDetail
              ? openStandaloneFromDetail(() =>
                  handleOpenEditActivity(activity),
                )
              : handleOpenEditActivity(activity),
        },
        {
          key: "next",
          label: "Marcar como proxima acao",
          icon: FlagIcon,
          hidden: activity.status !== "pending" || activity.isNextAction,
          onClick: () => handleMarkNextAction(activity),
        },
        {
          key: "done",
          label: "Concluir",
          icon: DoneIcon,
          hidden: activity.status === "done",
          onClick: () => handleUpdateActivityStatus(activity, "done"),
        },
        {
          key: "cancel",
          label: "Cancelar atividade",
          icon: CancelIcon,
          hidden: activity.status === "canceled",
          onClick: () => handleUpdateActivityStatus(activity, "canceled"),
        },
        {
          key: "delete",
          label: "Excluir atividade",
          icon: DeleteOutlineIcon,
          onClick: () =>
            closeDetail
              ? openStandaloneFromDetail(() => requestDeleteActivity(activity))
              : requestDeleteActivity(activity),
        },
      ]}
    />
  );

  if (availabilityLoading || !crmAvailability) {
    return (
      <MainContainer fullWidth>
        <Box className={classes.emptyState}>
          <CircularProgress size={28} />
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 12 }}
          >
            Carregando CRM...
          </Typography>
        </Box>
      </MainContainer>
    );
  }

  if (!crmAvailability.enabled) {
    return (
      <MainContainer fullWidth>
        <Box className={classes.root}>
          <Box className={classes.headerRow}>
            <Box className={classes.titleWrap}>
              <Typography className={classes.eyebrow}>Comercial</Typography>
              <Typography variant="h5" className={classes.title}>
                CRM comercial
              </Typography>
            </Box>
          </Box>
          <Paper className={classes.boardSection} elevation={0}>
            <Box className={classes.emptyState}>
              <Typography variant="h6">CRM indisponivel</Typography>
              <Typography variant="body2" color="textSecondary">
                O modulo esta desativado na configuracao tecnica deste ambiente.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer fullWidth>
      <ConfirmationModal
        title={confirmState.title}
        open={confirmState.open}
        onClose={() =>
          setConfirmState((current) => ({ ...current, open: false }))
        }
        onConfirm={() => {
          if (typeof confirmState.onConfirm === "function") {
            confirmState.onConfirm();
          }
        }}
      >
        {confirmState.message}
      </ConfirmationModal>

      <Box className={classes.root}>
        <Box className={classes.headerRow}>
          <Box className={classes.titleWrap}>
            <Typography className={classes.eyebrow}>Comercial</Typography>
            <Typography variant="h5" className={classes.title}>
              CRM comercial
            </Typography>
          </Box>

          <Box className={classes.actionsBar}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AutorenewIcon />}
              onClick={handleRefresh}
              disabled={loadingPipelines || loadingDeals || loadingMoreDeals}
            >
              Atualizar
            </Button>
            {canManagePipeline ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<BusinessCenterIcon />}
                  onClick={() => setPipelineManagerOpen(true)}
                >
                  Funis
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ViewColumnIcon />}
                  onClick={() => setStageManagerOpen(true)}
                  disabled={!selectedPipeline}
                >
                  Etapas
                </Button>
              </>
            ) : null}
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDeal}
              disabled={!selectedPipeline || activeStages.length === 0}
            >
              Novo negocio
            </Button>
          </Box>
        </Box>

        <MetricsStrip
          ariaLabel="Indicadores do CRM"
          items={[
            { key: "deals", label: "Negocios", value: dealsCount },
            {
              key: "open",
              label: dealsHasMore ? "Abertos exibidos" : "Abertos",
              value: summary.open,
            },
            {
              key: "won",
              label: dealsHasMore ? "Ganhos exibidos" : "Ganhos",
              value: summary.won,
            },
            {
              key: "lost",
              label: dealsHasMore ? "Perdidos exibidos" : "Perdidos",
              value: summary.lost,
            },
            {
              key: "amount",
              label: dealsHasMore ? "Valor exibido" : "Valor",
              value: formatCurrency(summary.amount),
            },
          ]}
        />

        <Paper className={classes.boardSection} elevation={0}>
          <Box className={classes.boardHeader}>
            <CrmToolbar
              pipelines={pipelines}
              selectedPipelineId={selectedPipelineId}
              searchParam={filters.searchParam}
              filterCount={activeAdvancedFiltersCount}
              filtersOpen={advancedFiltersOpen}
              loadingPipelines={loadingPipelines}
              onPipelineChange={setSelectedPipelineId}
              onSearchChange={(searchParam) =>
                setFilters((current) => ({ ...current, searchParam }))
              }
              onToggleFilters={(event) =>
                advancedFiltersOpen
                  ? handleCloseAdvancedFilters()
                  : handleOpenAdvancedFilters(event)
              }
            />

            <Box className={classes.boardMeta}>
              {movingDealId ? (
                <Chip
                  size="small"
                  label="Movendo negocio"
                  className={classes.warningChip}
                />
              ) : null}
              <Chip
                size="small"
                label={`${deals.length} de ${dealsCount} negocio${
                  dealsCount === 1 ? "" : "s"
                }`}
                className={classes.neutralChip}
              />
              <Chip
                size="small"
                label={
                  loadingDeals || loadingPipelines || loadingMoreDeals
                    ? "Atualizando"
                    : "Sincronizado"
                }
                className={
                  loadingDeals || loadingPipelines || loadingMoreDeals
                    ? classes.warningChip
                    : classes.neutralChip
                }
              />
            </Box>
          </Box>

          <CrmFilterPopover
            open={advancedFiltersOpen}
            anchorEl={filterAnchorEl}
            filters={draftFilters}
            users={users}
            contactOptions={contactOptions}
            contactsLoading={contactsLoading}
            onChange={(key, value) =>
              setDraftFilters((current) => ({ ...current, [key]: value }))
            }
            onContactSearch={setContactSearch}
            onClear={handleClearDraftFilters}
            onApply={handleApplyAdvancedFilters}
            onClose={handleCloseAdvancedFilters}
          />

          <AppliedFiltersBar
            filters={appliedFilterChips}
            onRemove={handleRemoveAdvancedFilter}
            onClearAll={handleClearAppliedFilters}
          />
          {(loadingDeals || loadingPipelines || loadingMoreDeals) && (
            <LinearProgress />
          )}

          {!selectedPipeline ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6">Nenhum funil selecionado</Typography>
              <Typography variant="body2" color="textSecondary">
                Crie um funil para comecar a organizar os negocios.
              </Typography>
            </Box>
          ) : stages.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6">Nenhuma etapa cadastrada</Typography>
              <Typography variant="body2" color="textSecondary">
                Cadastre etapas para habilitar o quadro do pipeline.
              </Typography>
            </Box>
          ) : (
            <Box className={`${classes.boardScroller} crm-board`}>
              <Board
                data={{
                  lanes: boardData.lanes.map((lane) => ({
                    ...lane,
                    style: {
                      backgroundColor:
                        lane.metadata.stage.isActive === false
                          ? theme.palette.action.hover
                          : hexToRgba(lane.metadata.stage.color, 0.08),
                      borderColor: hexToRgba(lane.metadata.stage.color, 0.24),
                    },
                    labelStyle: {
                      backgroundColor:
                        lane.metadata.stage.color || theme.palette.primary.main,
                      color: getReadableColor(
                        lane.metadata.stage.color || "#2f80ed",
                      ),
                      borderRadius: 999,
                      padding: "3px 9px",
                      fontWeight: 700,
                      fontSize: 12,
                    },
                  })),
                }}
                onCardMoveAcrossLanes={handleBoardCardMove}
                onCardClick={(cardId, metadata) =>
                  metadata?.onOpenDeal?.(metadata.deal)
                }
                laneDraggable={false}
                cardDraggable
                components={{ Card: DealCard }}
                style={{ backgroundColor: "transparent" }}
              />
            </Box>
          )}

          {selectedPipeline && stages.length > 0 && dealsCount > 0 ? (
            <Box className={classes.paginationBar}>
              <Typography variant="body2" color="textSecondary">
                Exibindo {deals.length} de {dealsCount} negocios filtrados.
              </Typography>
              {dealsHasMore ? (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleLoadMoreDeals}
                  disabled={loadingDeals || loadingMoreDeals}
                >
                  {loadingMoreDeals ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Carregar mais"
                  )}
                </Button>
              ) : (
                <Chip size="small" label="Todos os negocios carregados" />
              )}
            </Box>
          ) : null}
        </Paper>
      </Box>

      <Dialog
        open={canManagePipeline && pipelineManagerOpen}
        onClose={() => setPipelineManagerOpen(false)}
        fullWidth
        maxWidth="md"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>Funis</DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <Box className={classes.tablePaper}>
            <Box className={classes.tableHeader}>
              <Typography className={classes.tableHeaderTitle}>
                Pipelines
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenCreatePipeline}
              >
                Novo funil
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Ordem</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Padrao</TableCell>
                    <TableCell align="right" className={classes.actionsCell}>
                      Acoes
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pipelines.map((pipeline) => (
                    <TableRow key={pipeline.id}>
                      <TableCell>
                        <Typography style={{ fontWeight: 600 }}>
                          {pipeline.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {pipeline.description || "Sem descricao"}
                        </Typography>
                      </TableCell>
                      <TableCell>{pipeline.order ?? 0}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            pipeline.isActive === false ? "Inativo" : "Ativo"
                          }
                          style={
                            pipeline.isActive === false
                              ? { backgroundColor: "#e5e7eb", color: "#374151" }
                              : { backgroundColor: "#dcfce7", color: "#166534" }
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {pipeline.isDefault ? "Sim" : "Nao"}
                      </TableCell>
                      <TableCell align="right" className={classes.actionsCell}>
                        <RowActionsMenu
                          buttonTitle="Acoes do funil"
                          actions={[
                            {
                              key: "edit",
                              label: "Editar funil",
                              icon: EditIcon,
                              onClick: () => handleOpenEditPipeline(pipeline),
                            },
                            {
                              key: "delete",
                              label: "Excluir funil",
                              icon: DeleteOutlineIcon,
                              onClick: () => requestDeletePipeline(pipeline),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {loadingPipelines ? <TableRowSkeleton columns={5} /> : null}
                </TableBody>
              </Table>
            </TableContainer>
            {!loadingPipelines && pipelines.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography>Nenhum funil cadastrado.</Typography>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setPipelineManagerOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={canManagePipeline && pipelineFormOpen}
        onClose={() => !saving && setPipelineFormOpen(false)}
        fullWidth
        maxWidth="sm"
        classes={{ paper: classes.dialogPaper }}
      >
        <form onSubmit={handleSubmitPipeline}>
          <DialogTitle>
            {pipelineForm.id ? "Editar funil" : "Novo funil"}
          </DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  className={classes.field}
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  label="Nome"
                  value={pipelineForm.name}
                  onChange={(event) =>
                    setPipelineForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  className={classes.field}
                  fullWidth
                  required
                  type="number"
                  variant="outlined"
                  size="small"
                  label="Ordem"
                  value={pipelineForm.order}
                  onChange={(event) =>
                    setPipelineForm((current) => ({
                      ...current,
                      order: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  className={classes.field}
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  size="small"
                  label="Descricao"
                  value={pipelineForm.description}
                  onChange={(event) =>
                    setPipelineForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={pipelineForm.isActive}
                      onChange={(event) =>
                        setPipelineForm((current) => ({
                          ...current,
                          isActive: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Ativo"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={pipelineForm.isDefault}
                      onChange={(event) =>
                        setPipelineForm((current) => ({
                          ...current,
                          isDefault: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Pipeline padrao"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={() => setPipelineFormOpen(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={canManagePipeline && stageManagerOpen}
        onClose={() => setStageManagerOpen(false)}
        fullWidth
        maxWidth="md"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>Etapas do pipeline</DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <Box className={classes.tablePaper}>
            <Box className={classes.tableHeader}>
              <Box>
                <Typography className={classes.tableHeaderTitle}>
                  {selectedPipeline?.name || "Pipeline"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Reordene, edite ou desative etapas do funil selecionado.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateStage}
                disabled={!selectedPipeline}
              >
                Nova etapa
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Etapa</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Negocios</TableCell>
                    <TableCell align="right" className={classes.actionsCell}>
                      Acoes
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stages.map((stage, index) => (
                    <TableRow key={stage.id}>
                      <TableCell>{renderStageName(stage)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={stage.isActive === false ? "Inativa" : "Ativa"}
                          style={
                            stage.isActive === false
                              ? { backgroundColor: "#e5e7eb", color: "#374151" }
                              : { backgroundColor: "#dcfce7", color: "#166534" }
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {
                          deals.filter(
                            (deal) => Number(deal.stageId) === Number(stage.id),
                          ).length
                        }
                      </TableCell>
                      <TableCell align="right" className={classes.actionsCell}>
                        <RowActionsMenu
                          buttonTitle="Acoes da etapa"
                          disabled={saving}
                          actions={[
                            {
                              key: "up",
                              label: "Mover para cima",
                              icon: MoveToInboxIcon,
                              disabled: index === 0,
                              onClick: () => handleReorderStage(stage, -1),
                            },
                            {
                              key: "down",
                              label: "Mover para baixo",
                              icon: MoveToInboxIcon,
                              disabled: index === stages.length - 1,
                              onClick: () => handleReorderStage(stage, 1),
                            },
                            {
                              key: "edit",
                              label: "Editar etapa",
                              icon: EditIcon,
                              onClick: () => handleOpenEditStage(stage),
                            },
                            {
                              key: "toggle",
                              label:
                                stage.isActive === false
                                  ? "Ativar etapa"
                                  : "Desativar etapa",
                              icon: CancelIcon,
                              onClick: () => handleToggleStageActive(stage),
                            },
                            {
                              key: "delete",
                              label: "Excluir etapa",
                              icon: DeleteOutlineIcon,
                              onClick: () => requestDeleteStage(stage),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {stages.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography>Nenhuma etapa cadastrada.</Typography>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setStageManagerOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={canManagePipeline && stageFormOpen}
        onClose={() => !saving && setStageFormOpen(false)}
        fullWidth
        maxWidth="sm"
        classes={{ paper: classes.dialogPaper }}
      >
        <form onSubmit={handleSubmitStage}>
          <DialogTitle>
            {stageForm.id ? "Editar etapa" : "Nova etapa"}
          </DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <TextField
                  className={classes.field}
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  label="Nome"
                  value={stageForm.name}
                  onChange={(event) =>
                    setStageForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  className={classes.field}
                  fullWidth
                  required
                  type="number"
                  variant="outlined"
                  size="small"
                  label="Ordem"
                  value={stageForm.order}
                  onChange={(event) =>
                    setStageForm((current) => ({
                      ...current,
                      order: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  className={classes.field}
                  fullWidth
                  type="color"
                  variant="outlined"
                  size="small"
                  label="Cor"
                  value={stageForm.color || "#2f80ed"}
                  onChange={(event) =>
                    setStageForm((current) => ({
                      ...current,
                      color: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={stageForm.isActive}
                      onChange={(event) =>
                        setStageForm((current) => ({
                          ...current,
                          isActive: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Etapa ativa"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button onClick={() => setStageFormOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={dealFormOpen}
        onClose={() => !saving && setDealFormOpen(false)}
        fullWidth
        maxWidth="md"
        classes={{ paper: classes.dialogPaper }}
      >
        <form onSubmit={handleSubmitDeal}>
          <DialogTitle>
            {dealForm.id ? "Editar negocio" : "Novo negocio"}
          </DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <Box className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                Dados do negocio
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                    label="Titulo"
                    value={dealForm.title}
                    onChange={(event) =>
                      setDealForm((current) => ({
                        ...current,
                        title: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    variant="outlined"
                    size="small"
                    label="Valor"
                    value={dealForm.amount}
                    onChange={(event) =>
                      setDealForm((current) => ({
                        ...current,
                        amount: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Moeda"
                    value={dealForm.currency}
                    onChange={(event) =>
                      setDealForm((current) => ({
                        ...current,
                        currency: event.target.value.toUpperCase().slice(0, 3),
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    className={classes.field}
                    select
                    fullWidth
                    required
                    disabled={Boolean(dealForm.id)}
                    variant="outlined"
                    size="small"
                    label="Pipeline"
                    value={dealForm.pipelineId}
                    onChange={(event) =>
                      setDealForm((current) => {
                        const nextPipeline = pipelines.find(
                          (pipeline) =>
                            Number(pipeline.id) === Number(event.target.value),
                        );

                        return {
                          ...current,
                          pipelineId: event.target.value,
                          stageId: getDefaultStageId(nextPipeline),
                        };
                      })
                    }
                  >
                    {pipelines
                      .filter((pipeline) => pipeline.isActive !== false)
                      .map((pipeline) => (
                        <MenuItem key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    className={classes.field}
                    select
                    fullWidth
                    disabled={Boolean(dealForm.id)}
                    variant="outlined"
                    size="small"
                    label="Etapa inicial"
                    value={dealForm.stageId}
                    onChange={(event) =>
                      setDealForm((current) => ({
                        ...current,
                        stageId: event.target.value,
                      }))
                    }
                  >
                    {(
                      pipelines.find(
                        (pipeline) =>
                          Number(pipeline.id) === Number(dealForm.pipelineId),
                      )?.stages || []
                    )
                      .filter((stage) => stage.isActive !== false)
                      .sort(
                        (first, second) =>
                          Number(first.order || 0) - Number(second.order || 0),
                      )
                      .map((stage) => (
                        <MenuItem key={stage.id} value={stage.id}>
                          {stage.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="date"
                    label="Previsao de fechamento"
                    value={dealForm.expectedCloseAt}
                    onChange={(event) =>
                      setDealForm((current) => ({
                        ...current,
                        expectedCloseAt: event.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Origem"
                    value={dealForm.source}
                    onChange={(event) =>
                      setDealForm((current) => ({
                        ...current,
                        source: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    multiline
                    minRows={3}
                    variant="outlined"
                    size="small"
                    label="Descricao"
                    value={dealForm.description}
                    onChange={(event) =>
                      setDealForm((current) => ({
                        ...current,
                        description: event.target.value,
                      }))
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Box className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                Relacionamentos
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ContactAutocomplete
                    label="Contato"
                    value={dealForm.contact}
                    options={contactOptions}
                    loading={contactsLoading}
                    className={classes.field}
                    onInputChange={setContactSearch}
                    onChange={(contact) =>
                      setDealForm((current) => ({
                        ...current,
                        contact,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    className={classes.field}
                    select
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Responsavel"
                    value={dealForm.userId}
                    onChange={(event) =>
                      setDealForm((current) => ({
                        ...current,
                        userId: event.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">Sem responsavel</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button onClick={() => setDealFormOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={outcomeDialog.open}
        onClose={() =>
          !saving &&
          setOutcomeDialog({
            open: false,
            deal: null,
            status: "won",
            lossReason: "",
          })
        }
        fullWidth
        maxWidth="xs"
        classes={{ paper: classes.dialogPaper }}
      >
        <form onSubmit={handleSubmitOutcome}>
          <DialogTitle>
            {outcomeDialog.status === "won"
              ? "Registrar ganho"
              : "Registrar perda"}
          </DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <Typography variant="body2" color="textSecondary">
              {outcomeDialog.deal?.title}
            </Typography>
            {outcomeDialog.status === "lost" ? (
              <Box mt={2}>
                <TextField
                  className={classes.field}
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  variant="outlined"
                  size="small"
                  label="Motivo da perda"
                  value={outcomeDialog.lossReason}
                  onChange={(event) =>
                    setOutcomeDialog((current) => ({
                      ...current,
                      lossReason: event.target.value,
                    }))
                  }
                />
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={() =>
                setOutcomeDialog({
                  open: false,
                  deal: null,
                  status: "won",
                  lossReason: "",
                })
              }
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              Confirmar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={activityFormOpen}
        onClose={() => !saving && setActivityFormOpen(false)}
        fullWidth
        maxWidth="sm"
        classes={{ paper: classes.dialogPaper }}
      >
        <form onSubmit={handleSubmitActivity}>
          <DialogTitle>
            {activityForm.id ? "Editar atividade" : "Nova atividade"}
          </DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <TextField
                  className={classes.field}
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  label="Titulo"
                  value={activityForm.title}
                  onChange={(event) =>
                    setActivityForm((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={5}>
                <TextField
                  className={classes.field}
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Tipo"
                  value={activityForm.type}
                  onChange={(event) =>
                    setActivityForm((current) => ({
                      ...current,
                      type: event.target.value,
                    }))
                  }
                >
                  {ACTIVITY_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.field}
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="datetime-local"
                  label="Prazo"
                  value={activityForm.dueAt}
                  onChange={(event) =>
                    setActivityForm((current) => ({
                      ...current,
                      dueAt: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.field}
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Status"
                  value={activityForm.status}
                  onChange={(event) =>
                    setActivityForm((current) => ({
                      ...current,
                      status: event.target.value,
                    }))
                  }
                >
                  <MenuItem value="pending">Pendente</MenuItem>
                  <MenuItem value="done">Concluida</MenuItem>
                  <MenuItem value="canceled">Cancelada</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  className={classes.field}
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Responsavel"
                  value={activityForm.userId}
                  onChange={(event) =>
                    setActivityForm((current) => ({
                      ...current,
                      userId: event.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Sem responsavel</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  className={classes.field}
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  size="small"
                  label="Descricao"
                  value={activityForm.description}
                  onChange={(event) =>
                    setActivityForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={activityForm.isNextAction}
                      onChange={(event) =>
                        setActivityForm((current) => ({
                          ...current,
                          isNextAction: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Marcar como proxima acao"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={() => setActivityFormOpen(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <DealWorkspaceDialog
        open={detailOpen}
        loading={detailLoading}
        title={detailDeal?.title}
        status={
          detailDeal ? <StatusChip status={detailDeal.status} /> : null
        }
        stageName={detailDeal?.stage?.name}
        stages={activeStages}
        currentStageId={detailDeal?.stageId}
        stageChangeDisabled={
          saving ||
          movingDealId !== null ||
          detailDeal?.status !== "open" ||
          detailEditing ||
          Boolean(detailOutcome.status)
        }
        onStageChange={(stageId) =>
          detailDeal && handleMoveDealToStage(detailDeal, stageId)
        }
        onClose={handleCloseDealDetail}
        headerActions={
          detailDeal ? (
            <>
              {detailEditing ? (
                <React.Fragment key="inline-edit-actions">
                  <Button
                    size="small"
                    onClick={handleCancelInlineEdit}
                    disabled={saving}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    form="deal-inline-form"
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={saving}
                  >
                    {saving ? "Salvando..." : "Salvar"}
                  </Button>
                </React.Fragment>
              ) : (
                <React.Fragment key="view-actions">
                  <Button
                    id="deal-status-menu-button"
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<ChatBubbleOutlineIcon />}
                    onClick={() => handleOpenConversation(detailDeal)}
                    disabled={
                      Boolean(detailOutcome.status) ||
                      !detailDeal.contactId ||
                      !detailDeal.contact ||
                      Number(startingConversationDealId) ===
                        Number(detailDeal.id)
                    }
                  >
                    {Number(startingConversationDealId) ===
                    Number(detailDeal.id)
                      ? "Iniciando..."
                      : Number(activeDealConversation?.dealId) ===
                              Number(detailDeal.id) &&
                            activeDealConversation?.ticket?.uuid
                        ? "Abrir conversa"
                        : "Conversar"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={handleStartInlineEdit}
                    disabled={Boolean(detailOutcome.status)}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    aria-haspopup="menu"
                    aria-expanded={Boolean(statusMenuAnchorEl)}
                    aria-controls={
                      statusMenuAnchorEl ? "deal-status-menu" : undefined
                    }
                    onClick={(event) =>
                      setStatusMenuAnchorEl(event.currentTarget)
                    }
                    disabled={Boolean(detailOutcome.status)}
                  >
                    Alterar status
                  </Button>
                  <Menu
                    id="deal-status-menu"
                    anchorEl={statusMenuAnchorEl}
                    open={Boolean(statusMenuAnchorEl)}
                    onClose={() => setStatusMenuAnchorEl(null)}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    MenuListProps={{
                      "aria-labelledby": "deal-status-menu-button",
                    }}
                  >
                    {detailDeal.status === "open" ? (
                      <>
                        <MenuItem
                          onClick={() => handleStartInlineOutcome("won")}
                        >
                          Marcar como ganho
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleStartInlineOutcome("lost")}
                        >
                          Marcar como perdido
                        </MenuItem>
                      </>
                    ) : (
                      <MenuItem
                        onClick={() => handleStartInlineOutcome("open")}
                      >
                        Reabrir negocio
                      </MenuItem>
                    )}
                  </Menu>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      openStandaloneFromDetail(handleOpenCreateActivity)
                    }
                    disabled={Boolean(detailOutcome.status)}
                  >
                    Nova atividade
                  </Button>
                </React.Fragment>
              )}
            </>
          ) : null
        }
        summary={
          detailDeal ? (
            detailEditing ? (
              <DealInlineEditor
                formId="deal-inline-form"
                form={dealForm}
                saving={saving}
                error={dealFormError}
                pipelineName={selectedPipeline?.name}
                stageName={detailDeal.stage?.name}
                users={users}
                contactOptions={contactOptions}
                contactsLoading={contactsLoading}
                onContactSearch={setContactSearch}
                onChange={handleInlineDealFieldChange}
                onSubmit={handleSubmitDeal}
              />
            ) : (
              <>
                <DealSummary
                  items={[
                {
                  key: "contact",
                  label: "Contato",
                  value: detailDeal.contact?.name || "Sem contato",
                },
                {
                  key: "owner",
                  label: "Responsavel",
                  value: detailDeal.user?.name || "Sem responsavel",
                },
                {
                  key: "amount",
                  label: "Valor",
                  value: formatCurrency(
                    detailDeal.amount,
                    detailDeal.currency,
                  ),
                },
                {
                  key: "expectedClose",
                  label: "Previsao",
                  value: formatDate(detailDeal.expectedCloseAt),
                },
                {
                  key: "source",
                  label: "Origem",
                  value: detailDeal.source || "-",
                },
                {
                  key: "nextAction",
                  label: "Proxima acao",
                  alert: detailNextActionOverdue,
                  value: (
                    <Box>
                      <Typography
                        variant="body2"
                        className={
                          detailNextActionOverdue
                            ? classes.overdueText
                            : undefined
                        }
                      >
                        {detailDeal.nextActionNote
                          ? `${detailDeal.nextActionNote} - ${formatDateTime(
                              detailDeal.nextActionAt,
                            )}`
                          : "Sem proxima acao"}
                      </Typography>
                      {detailNextActionOverdue ? (
                        <Box mt={0.75}>
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<ErrorOutlineIcon />}
                            label="Acao atrasada"
                            className={classes.overdueChip}
                          />
                        </Box>
                      ) : null}
                    </Box>
                  ),
                },
                {
                  key: "closedAt",
                  label: "Fechamento",
                  value: formatDateTime(detailDeal.closedAt),
                },
                {
                  key: "lossReason",
                  label: "Motivo da perda",
                  value: detailDeal.lossReason || "-",
                },
                  ]}
                  description={detailDeal.description}
                />
                {detailOutcome.status ? (
                  <DealOutcomePanel
                    status={detailOutcome.status}
                    lossReason={detailOutcome.lossReason}
                    saving={saving}
                    error={detailOutcome.error}
                    onLossReasonChange={(lossReason) =>
                      setDetailOutcome((current) => ({
                        ...current,
                        lossReason,
                        error: "",
                      }))
                    }
                    onCancel={handleCancelInlineOutcome}
                    onConfirm={handleSubmitInlineOutcome}
                  />
                ) : null}
              </>
            )
          ) : null
        }
      >
        {detailDeal ? (
          <>
            <Box className={classes.workspaceTabs}>
              <Tabs
                value={detailTab}
                onChange={(event, value) => setDetailTab(value)}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                aria-label="Area de trabalho do negocio"
              >
                <Tab
                  id="deal-tab-activities"
                  value="activities"
                  aria-controls="deal-panel-activities"
                  aria-label={`Atividades, ${
                    (detailDeal.activities || []).length
                  } registros`}
                  label={
                    <span className={classes.workspaceTabLabel}>
                      Atividades
                      {(detailDeal.activities || []).length > 0 ? (
                        <span
                          className={classes.workspaceTabCount}
                          aria-hidden="true"
                        >
                          {(detailDeal.activities || []).length}
                        </span>
                      ) : null}
                    </span>
                  }
                />
                <Tab
                  id="deal-tab-conversations"
                  value="conversations"
                  aria-controls="deal-panel-conversations"
                  aria-label={`Conversas, ${conversationCount} registros`}
                  label={
                    <span className={classes.workspaceTabLabel}>
                      Conversas
                      {conversationCount > 0 ? (
                        <span
                          className={classes.workspaceTabCount}
                          aria-hidden="true"
                        >
                          {conversationCount}
                        </span>
                      ) : null}
                    </span>
                  }
                />
                <Tab
                  id="deal-tab-history"
                  value="history"
                  aria-controls="deal-panel-history"
                  aria-label={`Historico, ${detailHistoryCount} eventos`}
                  label={
                    <span className={classes.workspaceTabLabel}>
                      Historico
                      {detailHistoryCount > 0 ? (
                        <span
                          className={classes.workspaceTabCount}
                          aria-hidden="true"
                        >
                          {detailHistoryCount}
                        </span>
                      ) : null}
                    </span>
                  }
                />
              </Tabs>
            </Box>

            <Box className={classes.workspaceContent}>
              <Box
                id="deal-panel-activities"
                role="tabpanel"
                aria-labelledby="deal-tab-activities"
                hidden={detailTab !== "activities"}
              >
                <DealActivitiesPanel
                  activities={detailDeal.activities}
                  overdueCount={overdueActivitiesCount}
                  saving={saving}
                  formatDateTime={formatDateTime}
                  renderStatus={(status) => (
                    <ActivityStatusChip status={status} />
                  )}
                  renderActions={(activity) =>
                    renderActivityActions(activity, true)
                  }
                  onCreate={() =>
                    openStandaloneFromDetail(handleOpenCreateActivity)
                  }
                />
              </Box>

              <Box
                id="deal-panel-conversations"
                role="tabpanel"
                aria-labelledby="deal-tab-conversations"
                hidden={detailTab !== "conversations"}
              >
                <DealTicketsPanel
                  dealId={detailDeal.id}
                  contactName={detailDeal.contact?.name}
                  onActiveConversationChange={handleActiveConversationChange}
                  onCountChange={setConversationCount}
                />
              </Box>

              <Box
                id="deal-panel-history"
                role="tabpanel"
                aria-labelledby="deal-tab-history"
                hidden={detailTab !== "history"}
              >
                <DealHistoryPanel
                  deal={detailDeal}
                  formatDateTime={formatDateTime}
                />
              </Box>
            </Box>
          </>
        ) : null}
      </DealWorkspaceDialog>
    </MainContainer>
  );
};

export default Crm;
